import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useDispatch } from "react-redux";
import { Button, IconButton, Typography } from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
// import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloseIcon from "@mui/icons-material/Close";
import { setLoading } from "../actions";
import { isOnline, onNetworkFailure } from "../../utils/networkStatus";

const TOASTS = {
  offline: { Icon: WifiOffIcon, title: "You're offline", text: "Check your internet connection. The page is paused until you're back online." },
  online: { Icon: WifiIcon, title: "Connection restored", text: "You're back online. If something didn't load or save, refresh the page and try again." },
  // error: { Icon: CloudOffIcon, title: "Server unavailable", text: "We couldn't reach the server. Wait a moment and try again. If it keeps happening, contact support." },
};

const internetStatus =(callback) => {
  window.addEventListener("online", callback);
  window.addEventListener("offline", callback);
  return () => {
    window.removeEventListener("online", callback);
    window.removeEventListener("offline", callback);
  };
};

const NetworkStatusBanner = () => {
  const dispatch = useDispatch();
  const online = useSyncExternalStore(internetStatus, isOnline);
  const wasOffline = useRef(!online);
  const [toast, setToast] = useState(null); // { type, id } for the auto-hiding 'online' toast

  // useEffect(() => onNetworkFailure(() => {
  //   // reset the global redux loader too
  //   dispatch(setLoading(false));
  //   // while offline the offline toast already explains the failure
  //   if (isOnline()) setToast({ type: "error", id: Date.now() });
  // }), [dispatch]);
  // reset the global redux loader when a request fails without a response
  useEffect(() => onNetworkFailure(() => dispatch(setLoading(false))), [dispatch]);

  useEffect(() => {
    if (!online) wasOffline.current = true;
    else if (wasOffline.current) {
      wasOffline.current = false;
      setToast({ type: "online", id: Date.now() });
    }
  }, [online]);

  useEffect(() => {
    if (online) return;
    // the overlay only stops the mouse; also stop typing / Enter-to-submit in a focused field
    document.activeElement?.blur?.();
    const blockKeys = (e) => {
      // keep browser shortcuts (reload, zoom, devtools) working
      if (e.ctrlKey || e.metaKey || /^F\d+$/.test(e.key)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", blockKeys, true);
    return () => window.removeEventListener("keydown", blockKeys, true);
  }, [online]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const type = online ? toast?.type : "offline";
  if (!type) return null;
  const { Icon, title, text } = TOASTS[type];

  return (
    <>
      {type === "offline" ? <div className="network-offline-overlay" aria-hidden /> : null}
      <div key={toast?.id ?? type} className={`network-toast network-toast--${type}`} role={type === "online" ? "status" : "alert"} aria-live={type === "online" ? "polite" : "assertive"}>
        <div className="network-toast__icon"> <Icon fontSize="small" /> </div>
        <div>
          <Typography variant="body2" className="network-toast__title">{title}</Typography>
          <Typography variant="caption" className="network-toast__text">{text}</Typography>
        </div>
        {type === "online" ? (
          <Button size="small" className="network-toast__action" onClick={() => window.location.reload()}>Refresh</Button>
        ) : null}
        {type !== "offline" ? (
          <IconButton size="small" className="network-toast__close" aria-label="Dismiss" onClick={() => setToast(null)}> <CloseIcon fontSize="small" /> </IconButton>
        ) : null}
      </div>
    </>
  );
};

export default NetworkStatusBanner;